import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import PlayerHand from './PlayerHand';
import Card, { MiniCard, ItemTypes } from './Card';
import PhaseBuilder from './PhaseBuilder';
import DrawDiscardPiles from './DrawDiscardPiles';
import ScoreBoard from './ScoreBoard';
import GameLog from './GameLog';
import { RoundEndScreen, GameEndScreen } from './RoundEndScreen';
import { getPhaseInfo, getPhasesForMode } from '../utils/phaseDefinitions';
import { canHitOnPhase } from '../utils/cardHelpers';
import { useAudio } from '../contexts/AudioContext';
import { getCPUAvatar, getCPUGradient } from '../utils/avatarHelpers';

function GameBoard({
  game,
  playerId,
  roomCode,
  room,
  cpuThinking,
  gameLogs,
  gamePaused,
  onDrawCard,
  onLayDownPhase,
  onHitCard,
  onDiscardCard,
  onSelectPhase,
  onStartNewRound,
  onRematch,
  onLeaveRoom
}) {
  const [selectedCards, setSelectedCards] = useState([]);
  const [error, setError] = useState(null);
  const [showScoreboard, setShowScoreboard] = useState(false);
  const [showPhaseSelector, setShowPhaseSelector] = useState(false);
  const [showSkipTargetSelector, setShowSkipTargetSelector] = useState(false);
  const [pendingSkipCard, setPendingSkipCard] = useState(null);
  const [lastDrawnCardId, setLastDrawnCardId] = useState(null);

  // Phase builder state - persistent groups for drag-and-drop phase building
  const [phaseBuilderGroups, setPhaseBuilderGroups] = useState([]);

  // Undo history for phase builder
  const [phaseBuilderHistory, setPhaseBuilderHistory] = useState([]);
  const maxHistorySize = 20;

  // ─── Deal animation ─────────────────────────────────────────────────────
  // dealKey changes every new round => PlayerHand remounts => isDealing triggers fresh
  const dealKey   = game?.phase === 'playing' ? `deal-r${game?.roundNumber ?? 0}` : 'idle';
  const [dealAnimation, setDealAnimation]   = useState(false);
  const prevDealKeyRef = useRef('');

  useEffect(() => {
    if (game?.phase === 'playing' && dealKey !== prevDealKeyRef.current) {
      prevDealKeyRef.current = dealKey;
      const t = setTimeout(() => setDealAnimation(true), 80);
      const t2 = setTimeout(() => setDealAnimation(false), 3200);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
  }, [game?.phase, dealKey]);
  // ─────────────────────────────────────────────────────────────────────────

  // Sound effects
  const { playSfx } = useAudio();
  const prevTurnPlayerId = useRef(game?.currentPlayerId);

  // Find current player data
  const currentPlayer = useMemo(() =>
    game?.players?.find(p => p.id === playerId),
    [game, playerId]
  );

  // Other players
  const otherPlayers = useMemo(() =>
    game?.players?.filter(p => p.id !== playerId) || [],
    [game, playerId]
  );

  // Is it my turn?
  const isMyTurn = game?.currentPlayerId === playerId;

  // Turn phase
  const turnPhase = game?.turnPhase;

  // Can I draw?
  const canDraw = isMyTurn && turnPhase === 'draw';

  // Can I play/discard?
  const canPlay = isMyTurn && turnPhase === 'play';

  // Have I completed my phase this round?
  const hasCompletedPhase = currentPlayer?.completedPhaseThisRound;

  // My current phase info
  const myPhaseInfo = getPhaseInfo(currentPlayer?.currentPhase);

  // Calculate which cards can hit on visible phase groups (hit preview)
  const hittableCardIds = useMemo(() => {
    if (!currentPlayer?.hand || !hasCompletedPhase || !canPlay) {
      return new Set();
    }

    const hittableIds = new Set();

    // Get all players who have laid down phases
    const playersWithPhases = game?.players?.filter(p => p.laidDownPhase) || [];

    for (const card of currentPlayer.hand) {
      for (const targetPlayer of playersWithPhases) {
        const targetPhaseInfo = getPhaseInfo(targetPlayer.currentPhase);
        if (!targetPhaseInfo) continue;

        for (let groupIdx = 0; groupIdx < targetPlayer.laidDownPhase.length; groupIdx++) {
          const group = targetPlayer.laidDownPhase[groupIdx];
          const groupType = targetPhaseInfo.requirements[groupIdx];

          const result = canHitOnPhase(card, group, groupType);
          if (result.canHit) {
            hittableIds.add(card.id);
            break; // Card can hit somewhere, no need to check more
          }
        }

        if (hittableIds.has(card.id)) break; // Already found a hit target
      }
    }

    return hittableIds;
  }, [currentPlayer?.hand, game?.players, hasCompletedPhase, canPlay]);

  // Initialize phase builder groups when phase info changes
  React.useEffect(() => {
    if (myPhaseInfo && !hasCompletedPhase) {
      setPhaseBuilderGroups(myPhaseInfo.requirements.map(() => []));
      setPhaseBuilderHistory([]); // Clear history on new phase/round
    } else {
      setPhaseBuilderGroups([]);
      setPhaseBuilderHistory([]);
    }
  }, [myPhaseInfo, hasCompletedPhase, game?.roundNumber]);

  // Helper to save current state to history before making changes
  const saveToHistory = useCallback(() => {
    setPhaseBuilderHistory(prev => {
      const newHistory = [...prev, phaseBuilderGroups];
      // Keep only the last N states
      if (newHistory.length > maxHistorySize) {
        return newHistory.slice(-maxHistorySize);
      }
      return newHistory;
    });
  }, [phaseBuilderGroups, maxHistorySize]);

  // Undo last phase builder action
  const undoPhaseBuilder = useCallback(() => {
    if (phaseBuilderHistory.length === 0) return;

    const previousState = phaseBuilderHistory[phaseBuilderHistory.length - 1];
    setPhaseBuilderGroups(previousState);
    setPhaseBuilderHistory(prev => prev.slice(0, -1));
  }, [phaseBuilderHistory]);

  // Check if undo is available
  const canUndo = phaseBuilderHistory.length > 0;

  // Sync phase builder with hand - remove cards that are no longer in hand
  React.useEffect(() => {
    if (!currentPlayer?.hand) return;
    const handCardIds = new Set(currentPlayer.hand.map(c => c.id));

    setPhaseBuilderGroups(prev => {
      let changed = false;
      const updated = prev.map(group => {
        const filteredGroup = group.filter(card => handCardIds.has(card.id));
        if (filteredGroup.length !== group.length) changed = true;
        return filteredGroup;
      });
      return changed ? updated : prev;
    });
  }, [currentPlayer?.hand]);

  // Cards currently in the phase builder
  const cardsInBuilder = useMemo(() => {
    const cardIds = new Set();
    phaseBuilderGroups.forEach(group => {
      group.forEach(card => cardIds.add(card.id));
    });
    return cardIds;
  }, [phaseBuilderGroups]);

  // Cards available for hand display (excluding those in builder)
  const availableHandCards = useMemo(() => {
    return (currentPlayer?.hand || []).filter(c => !cardsInBuilder.has(c.id));
  }, [currentPlayer?.hand, cardsInBuilder]);

  // Phase builder functions
  const addCardToPhaseBuilder = useCallback((cardOrCards, groupIndex) => {
    saveToHistory();
    const cardsToAdd = Array.isArray(cardOrCards) ? cardOrCards : [cardOrCards];
    const cardsIdsToAdd = new Set(cardsToAdd.map(c => c.id));

    setPhaseBuilderGroups(prev => prev.map((group, idx) => {
      if (idx === groupIndex) {
        // Add to this group if not already there
        const newCards = cardsToAdd.filter(c => !group.find(existing => existing.id === c.id));
        return [...group, ...newCards];
      }
      // Remove from other groups (in case cards were dragged between groups)
      return group.filter(c => !cardsIdsToAdd.has(c.id));
    }));
    // Clear selection when card is added to builder
    setSelectedCards([]);
  }, [saveToHistory]);

  // Add card(s) at a specific position (for runs)
  const addCardToPhaseBuilderAtPosition = useCallback((cardOrCards, groupIndex, position) => {
    saveToHistory();
    const cardsToAdd = Array.isArray(cardOrCards) ? cardOrCards : [cardOrCards];
    const cardsIdsToAdd = new Set(cardsToAdd.map(c => c.id));
    setPhaseBuilderGroups(prev => prev.map((group, idx) => {
      if (idx === groupIndex) {
        // Remove all cards that we are adding (so we can insert them at the correct spot)
        const filtered = group.filter(c => !cardsIdsToAdd.has(c.id));
        
        // Count how many of the dragged cards were originally *before* the insertion point
        let shiftAmount = 0;
        cardsToAdd.forEach(cardToInsert => {
          const sourceIndex = group.findIndex(c => c.id === cardToInsert.id);
          if (sourceIndex !== -1 && sourceIndex < position) {
            shiftAmount += 1;
          }
        });
        
        const adjustedPosition = Math.max(0, position - shiftAmount);

        // Insert at the specified position
        const newGroup = [...filtered];
        newGroup.splice(adjustedPosition, 0, ...cardsToAdd);
        return newGroup;
      }
      // Remove from other groups
      return group.filter(c => !cardsIdsToAdd.has(c.id));
    }));
    setSelectedCards([]);
  }, [saveToHistory]);

  const removeCardFromPhaseBuilder = useCallback((cardId, groupIndex) => {
    saveToHistory();
    setPhaseBuilderGroups(prev => prev.map((group, idx) => {
      if (idx === groupIndex) {
        return group.filter(c => c.id !== cardId);
      }
      return group;
    }));
  }, [saveToHistory]);

  // Return a card from the builder back to hand (just removes it from builder)
  const returnCardToHand = useCallback((card, fromGroupIndex) => {
    if (fromGroupIndex !== null && fromGroupIndex !== undefined) {
      removeCardFromPhaseBuilder(card.id, fromGroupIndex);
    }
  }, [removeCardFromPhaseBuilder]);

  const clearPhaseBuilder = useCallback(() => {
    if (myPhaseInfo) {
      saveToHistory();
      setPhaseBuilderGroups(myPhaseInfo.requirements.map(() => []));
    }
  }, [myPhaseInfo, saveToHistory]);

  // Handle phase out (submit the phase from builder)
  const handlePhaseOut = useCallback(async () => {
    try {
      setError(null);
      await onLayDownPhase(phaseBuilderGroups);
      playSfx('phase_complete');
      setPhaseBuilderGroups([]);
      setSelectedCards([]);
    } catch (err) {
      setError(err);
    }
  }, [onLayDownPhase, phaseBuilderGroups, playSfx]);

  // Handle drawing a card
  const handleDraw = useCallback(async (source) => {
    try {
      setError(null);
      const result = await onDrawCard(source);
      // Track the drawn card for highlighting
      if (result?.card?.id) {
        setLastDrawnCardId(result.card.id);
        playSfx('draw');
        // Clear the highlight after a few seconds
        setTimeout(() => setLastDrawnCardId(null), 3000);
      }
    } catch (err) {
      setError(err);
    }
  }, [onDrawCard, playSfx]);

  // Handle hitting
  // position is optional - 'start' or 'end' for wilds on runs
  const handleHit = useCallback(async (targetPlayerId, cardId, groupIndex, position = null) => {
    try {
      setError(null);
      await onHitCard(targetPlayerId, cardId, groupIndex, position);
      setSelectedCards([]);
    } catch (err) {
      setError(err);
    }
  }, [onHitCard]);

  // Handle discarding (works with card ID or card object for drag-and-drop)
  const handleDiscard = useCallback(async (cardOrId, targetPlayerId = null) => {
    // Support both card object (from drag-and-drop) and card ID
    const card = typeof cardOrId === 'object' ? cardOrId : currentPlayer?.hand?.find(c => c.id === cardOrId);
    const cardId = typeof cardOrId === 'object' ? cardOrId.id : cardOrId;

    if (!card) {
      setError('Card not found');
      return;
    }

    // Check if it's a skip card and needs target selection
    if (card.type === 'skip' && !targetPlayerId) {
      // Show skip target selector
      setPendingSkipCard(card);
      setShowSkipTargetSelector(true);
      return;
    }

    try {
      setError(null);
      await onDiscardCard(cardId, targetPlayerId);
      playSfx('play');
      setSelectedCards([]);
      setPendingSkipCard(null);
      setShowSkipTargetSelector(false);
    } catch (err) {
      setError(err);
    }
  }, [onDiscardCard, currentPlayer, playSfx]);

  // Handle skip target selection
  const handleSkipTargetSelect = useCallback(async (targetId) => {
    if (!pendingSkipCard) return;
    try {
      setError(null);
      await onDiscardCard(pendingSkipCard.id, targetId);
      playSfx('play');
      setSelectedCards([]);
      setPendingSkipCard(null);
      setShowSkipTargetSelector(false);
    } catch (err) {
      setError(err);
    }
  }, [onDiscardCard, pendingSkipCard, playSfx]);

  // Cancel skip target selection
  const handleCancelSkipTarget = useCallback(() => {
    setPendingSkipCard(null);
    setShowSkipTargetSelector(false);
  }, []);

  // Handle phase selection (for Choice mode)
  const handleSelectPhase = useCallback(async (phaseNumber) => {
    try {
      setError(null);
      await onSelectPhase(phaseNumber);
      setShowPhaseSelector(false);
    } catch (err) {
      setError(err);
    }
  }, [onSelectPhase]);

  // Clear error after timeout
  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Show phase selector for Choice mode
  React.useEffect(() => {
    if (game?.phase === 'phaseSelection' && game?.pendingPhaseSelections?.includes(playerId)) {
      setShowPhaseSelector(true);
    }
  }, [game?.phase, game?.pendingPhaseSelections, playerId]);

  // Play sound when it becomes your turn
  React.useEffect(() => {
    const currentTurnId = game?.currentPlayerId;
    // Only play sound when turn changes to this player
    if (currentTurnId === playerId && prevTurnPlayerId.current !== playerId && game?.phase === 'playing') {
      playSfx('play');
    }
    prevTurnPlayerId.current = currentTurnId;
  }, [game?.currentPlayerId, playerId, playSfx, game?.phase]);

  // Play sound when game ends
  React.useEffect(() => {
    if (game?.phase === 'gameEnd') {
      if (game?.winner === playerId) {
        playSfx('win');
      }
    }
  }, [game?.phase, game?.winner, playerId, playSfx]);

  // Game end screen
  if (game?.phase === 'gameEnd') {
    return (
      <GameEndScreen
        game={game}
        playerId={playerId}
        room={room}
        onRematch={onRematch}
        onLeaveRoom={onLeaveRoom}
      />
    );
  }

  // Paused screen (when a player disconnects)
  if (game?.phase === 'paused' || gamePaused) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="bg-gray-800 rounded-xl p-8 text-center max-w-md">
          <div className="text-5xl mb-4">⏸️</div>
          <h1 className="text-3xl font-bold text-amber-500 mb-4">Game Paused</h1>
          <p className="text-gray-300 mb-2">
            {gamePaused?.pausedByPlayerName || 'A player'} has disconnected.
          </p>
          <p className="text-gray-400 text-sm">
            The game will resume when they reconnect.
          </p>
          <button
            onClick={onLeaveRoom}
            className="btn-secondary mt-6"
          >
            Leave Game
          </button>
        </div>
      </div>
    );
  }

  // Round end screen
  if (game?.phase === 'roundEnd') {
    return (
      <RoundEndScreen
        game={game}
        playerId={playerId}
        room={room}
        onStartNewRound={onStartNewRound}
        onLeaveRoom={onLeaveRoom}
      />
    );
  }

  // Phase selection modal (for Choice mode)
  if (showPhaseSelector) {
    const availablePhases = getPhasesForMode(game?.mode);

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h2 className="text-2xl font-bold mb-4">Select Your Phase</h2>
          <p className="text-gray-400 mb-6">
            Choose which phase you want to attempt this round
          </p>

          <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
            {availablePhases.map(phaseNum => {
              const info = getPhaseInfo(phaseNum);
              return (
                <button
                  key={phaseNum}
                  onClick={() => handleSelectPhase(phaseNum)}
                  className="p-4 bg-white/5 hover:bg-white/10 rounded-lg text-left transition-colors"
                >
                  <div className="font-bold text-accent-gold">Phase {phaseNum}</div>
                  <div className="text-sm text-gray-300">{info?.name}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen flex flex-col relative bg-cover bg-center bg-no-repeat bg-game-bg shadow-inner shadow-black/30"
      style={{ backgroundImage: "url('/mahjong/game_bg_new.png')", overflow: 'hidden' }}
    >
      {/* 确保全屏绿底之上，没有任何其他不必要的模糊白雾影响了游戏性能或清晰度 */}
      {/* 错误通知提示 */}
      {error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          {error}
        </div>
      )}

      {/* GameLog moved to the bottom alongside DrawDiscardPiles to prevent overlap */}

      {/* Header - responsive, with embedded turn indicator */}
      <header style={{
        display: 'flex',
        alignItems: 'center',
        padding: '6px 12px',
        borderBottom: '1px solid rgba(212,168,67,0.15)',
        justifyContent: 'space-between',
        gap: '8px',
        background: 'rgba(13,31,20,0.6)',
        backdropFilter: 'blur(8px)',
      }}>
        {/* Left: Phase indicator */}
        <div className="group cursor-help relative" style={{ flex: '0 0 auto' }}>
          <button style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '5px 14px',
            borderRadius: '6px',
            border: hasCompletedPhase
              ? '1px solid rgba(39,174,96,0.6)'
              : '1px solid rgba(212,168,67,0.5)',
            background: hasCompletedPhase
              ? 'rgba(39,174,96,0.12)'
              : 'rgba(212,168,67,0.12)',
            color: hasCompletedPhase ? '#27ae60' : '#d4a843',
            fontFamily: '"Cinzel", serif',
            fontSize: '0.8rem',
            fontWeight: '700',
            letterSpacing: '0.08em',
            cursor: 'help',
          }}>
            Phase {currentPlayer?.currentPhase}
            {hasCompletedPhase && <span style={{ color: '#27ae60' }}>✓</span>}
            <span style={{
              fontSize: '0.65rem',
              opacity: 0.6,
              border: '1px solid currentColor',
              borderRadius: '3px',
              padding: '0 3px',
              lineHeight: '1.4',
            }}>?</span>
          </button>
          {/* Tooltip */}
          <div style={{
            position: 'absolute',
            top: '110%',
            left: 0,
            minWidth: '160px',
            padding: '8px 10px',
            background: 'rgba(13,31,20,0.97)',
            border: '1px solid rgba(212,168,67,0.3)',
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
            fontSize: '0.72rem',
            color: '#f5ead2',
            opacity: 0,
            pointerEvents: 'none',
            zIndex: 50,
          }} className="group-hover:opacity-100 transition-opacity">
            {myPhaseInfo?.name}
          </div>
        </div>

        {/* Center: Turn Indicator (compact badge in header) */}
        <div style={{ flex: '1 1 0', display: 'flex', justifyContent: 'center' }}>
          {isMyTurn ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '3px 12px', borderRadius: '20px',
              background: 'rgba(212,168,67,0.2)',
              border: '1px solid rgba(212,168,67,0.6)',
              color: '#d4a843', fontFamily: '"Cinzel",serif',
              fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.06em',
              whiteSpace: 'nowrap',
            }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#e74c3c', display: 'inline-block', animation: 'ping 1s cubic-bezier(0,0,0.2,1) infinite' }} />
              {turnPhase === 'draw' ? 'DRAW A CARD' : hasCompletedPhase ? 'HIT OR DISCARD' : 'BUILD OR DISCARD'}
            </span>
          ) : (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              padding: '3px 12px', borderRadius: '20px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(245,234,210,0.4)',
              fontFamily: '"Cinzel",serif', fontSize: '0.62rem',
              whiteSpace: 'nowrap',
            }}>
              {cpuThinking
                ? `${game?.players?.find(p => p.id === game?.currentPlayerId)?.name} thinking…`
                : `${game?.players?.find(p => p.id === game?.currentPlayerId)?.name}'s turn`}
            </span>
          )}
        </div>

        {/* Right: Scores + Leave */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '0 0 auto' }}>
          <button
            onClick={() => setShowScoreboard(!showScoreboard)}
            style={{
              padding: '5px 14px',
              borderRadius: '6px',
              border: '1px solid rgba(212,168,67,0.5)',
              background: showScoreboard ? 'rgba(212,168,67,0.2)' : 'rgba(212,168,67,0.08)',
              color: '#d4a843',
              fontFamily: '"Cinzel", serif',
              fontSize: '0.8rem',
              fontWeight: '700',
              letterSpacing: '0.08em',
              cursor: 'pointer',
              transition: 'background 0.2s ease',
            }}
          >
            SCORES
          </button>
          <button
            onClick={onLeaveRoom}
            title="Leave Game"
            style={{
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              border: '1px solid rgba(192,57,43,0.5)',
              background: 'rgba(192,57,43,0.08)',
              color: '#c0392b',
              fontSize: '1.1rem',
              cursor: 'pointer',
              transition: 'background 0.2s ease',
            }}
          >
            ✕
          </button>
        </div>
      </header>

      {/* Scoreboard overlay */}
      {showScoreboard && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center" onClick={() => setShowScoreboard(false)}>
          <div onClick={e => e.stopPropagation()}>
            <ScoreBoard players={game?.players} playerId={playerId} />
          </div>
        </div>
      )}

      {/* Main game area */}
      <div className="flex-1 flex flex-col lg:flex-row gap-2 p-2 overflow-auto">
        {/* Other players - compact horizontal strip, padding ensures outline isn't clipped */}
        <div
          className="flex lg:flex-col gap-1 overflow-x-auto pb-1 lg:pb-0 lg:w-48 justify-center"
          style={{ flexShrink: 0, padding: '4px', margin: '-4px' }}
        >
          {otherPlayers.map(player => (
            <OtherPlayerPanel
              key={player.id}
              player={player}
              isCurrentTurn={game?.currentPlayerId === player.id}
              cpuThinking={cpuThinking === player.id}
              myPhaseCompleted={hasCompletedPhase}
              canHitEnabled={canPlay && hasCompletedPhase}
              onHit={handleHit}
              selectedCard={selectedCards[0]}
            />
          ))}
        </div>

        {/* Center - Game table */}
        <div className="flex-1 flex flex-col items-center justify-center gap-2">
          {/* Phase Builder - center of the game area when not completed */}
          {!hasCompletedPhase && myPhaseInfo && phaseBuilderGroups.length > 0 && (
            <div className="w-full max-w-2xl px-2 sm:px-4 z-10 relative">
              <PhaseBuilder
                phaseNumber={currentPlayer?.currentPhase}
                phaseInfo={myPhaseInfo}
                groups={phaseBuilderGroups}
                onAddCard={addCardToPhaseBuilder}
                onAddCardAtPosition={addCardToPhaseBuilderAtPosition}
                onRemoveCard={removeCardFromPhaseBuilder}
                onClearAll={clearPhaseBuilder}
                onUndo={undoPhaseBuilder}
                canUndo={canUndo}
                onPhaseOut={handlePhaseOut}
                canPhaseOut={canPlay}
                disabled={false}
                onZoneClick={(groupIdx) => {
                  if (selectedCards.length > 0) {
                    addCardToPhaseBuilder(selectedCards, groupIdx);
                  }
                }}
              />
            </div>
          )}

          {/* Completed phase - for hitting on self */}
          {hasCompletedPhase && currentPlayer?.laidDownPhase && (
            <div className="w-full max-w-xl px-2 sm:px-4">
              <div className="bg-green-900/20 rounded-lg p-3 border border-green-500/30">
                <div className="text-center text-green-400 text-sm mb-2">
                  Your Completed Phase {currentPlayer?.currentPhase}
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {currentPlayer.laidDownPhase.map((group, groupIdx) => (
                    <HitDropZone
                      key={groupIdx}
                      playerId={playerId}
                      groupIndex={groupIdx}
                      group={group}
                      phaseInfo={myPhaseInfo}
                      canHitEnabled={canPlay && hasCompletedPhase}
                      checkCanHit={(card, grp, idx) => {
                        const groupType = myPhaseInfo.requirements[idx];
                        if (!groupType) return false;
                        const result = canHitOnPhase(card, grp, groupType);
                        return result.canHit;
                      }}
                      selectedCard={selectedCards[0]}
                      onHit={handleHit}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Turn indicator removed from here - now in Header */}

          {/* Draw/Discard piles & GameLog */}
          <div className="w-full relative flex justify-center items-end mt-auto pt-4 pb-2">
            
            <DrawDiscardPiles
              deck={game?.deck}
              canDraw={canDraw}
              canDiscard={canPlay}
              onDrawFromDeck={() => handleDraw('deck')}
              onDrawFromDiscard={() => handleDraw('discard')}
              onDiscard={handleDiscard}
            />

            {/* GameLog is anchored to the right side of this bottom area so it moves down when table area expands */}
            <div className={`absolute right-0 bottom-2 sm:right-4 z-40 pl-2 transition-opacity duration-300 ${selectedCards.length > 0 ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}>
              <GameLog logs={gameLogs} />
            </div>
          </div>
        </div>
      </div>
      {/* Player's hand — sticky at bottom, always visible regardless of content height */}
      <div
        className="relative"
        style={{
          flexShrink: 0,
          position: 'sticky',
          bottom: 0,
          zIndex: 50,
          background: 'rgba(5,20,10,0.92)',
          backdropFilter: 'blur(8px)',
          borderTop: '1px solid rgba(212,168,67,0.15)',
          paddingBottom: 'env(safe-area-inset-bottom, 4px)',
        }}
      >

      <HandDropZone
        onReturnCard={returnCardToHand}
        hasBuilder={!hasCompletedPhase && myPhaseInfo && phaseBuilderGroups.length > 0}
      >
        <PlayerHand
          key={dealKey}
          cards={availableHandCards}
          selectedCards={selectedCards}
          onCardSelect={setSelectedCards}
          disabled={false}
          canSelect={true}
          maxSelect={Infinity}
          highlightCardId={lastDrawnCardId}
          hittableCardIds={hittableCardIds}
          dealAnimation={dealAnimation}
        />
      </HandDropZone>
      </div>

      {/* Skip target selector modal */}
      {showSkipTargetSelector && pendingSkipCard && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="text-2xl font-bold mb-4 text-accent-gold">Skip a Player</h2>
            <p className="text-gray-400 mb-6">
              Choose which player to skip. They will lose their next turn.
            </p>

            <div className="space-y-3">
              {otherPlayers.map(player => (
                <button
                  key={player.id}
                  onClick={() => handleSkipTargetSelect(player.id)}
                  className="w-full p-4 rounded-lg text-left transition-colors flex items-center gap-3 bg-white/5 hover:bg-white/10 cursor-pointer"
                >
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg
                      ${!player.isComputer ? 'bg-blue-600' : ''}
                    `}
                    style={player.isComputer ? { background: getCPUGradient(player.id || player.name) } : {}}
                  >
                    {player.isComputer ? getCPUAvatar(player.id || player.name) : player.name[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium text-white">
                      {player.name}
                    </div>
                    <div className="text-xs text-gray-400">
                      {player.skipCount > 0
                        ? `Skipped ${player.skipCount}x - ${player.handCount} cards`
                        : `${player.handCount} cards in hand`}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <button
              onClick={handleCancelSkipTarget}
              className="btn-secondary w-full mt-6"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Other player panel component - compact for mobile
function OtherPlayerPanel({
  player,
  isCurrentTurn,
  cpuThinking,
  myPhaseCompleted,
  canHitEnabled,
  onHit,
  selectedCard
}) {
  const phaseInfo = getPhaseInfo(player.currentPhase);

  // Check if a card can hit on a specific group
  const checkCanHit = useCallback((card, group, groupIdx) => {
    if (!card || !phaseInfo) return false;
    const groupType = phaseInfo.requirements[groupIdx];
    if (!groupType) return false;
    const result = canHitOnPhase(card, group, groupType);
    return result.canHit;
  }, [phaseInfo]);

  return (
    <div
      style={{
        background: isCurrentTurn ? 'rgba(212,168,67,0.1)' : 'rgba(255,255,255,0.04)',
        borderRadius: '8px',
        padding: '6px 10px',
        minWidth: '130px',
        maxWidth: '220px',
        flex: '1 1 0',
        outline: isCurrentTurn ? '2px solid #d4a843' : '1px solid rgba(255,255,255,0.08)',
        outlineOffset: '0px',
        boxShadow: isCurrentTurn ? '0 0 12px rgba(212,168,67,0.3)' : 'none',
        transition: 'outline 0.2s ease, box-shadow 0.3s ease, background 0.2s ease',
      }}
    >
      {/* Header row: avatar + name (secondary) + score */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
        {/* Avatar */}
        <div style={{
          width: '22px', height: '22px', borderRadius: '50%',
          background: player.isComputer ? getCPUGradient(player.id || player.name) : 'rgba(212,168,67,0.25)',
          border: `1.5px solid ${isCurrentTurn ? '#d4a843' : 'rgba(255,255,255,0.12)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: player.isComputer ? '0.65rem' : '0.55rem', fontWeight: '700', flexShrink: 0,
          color: '#f5ead2',
          paddingBottom: player.isComputer ? '1px' : '0',
        }}>
          {player.isComputer ? getCPUAvatar(player.id || player.name) : player.name[0].toUpperCase()}
        </div>

        {/* Name — secondary, small */}
        <span style={{
          fontSize: '0.62rem', color: 'rgba(245,234,210,0.45)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          flex: '1 1 0',
        }}>
          {player.name}
          {cpuThinking && <span style={{ color: '#d4a843', marginLeft: '2px' }}>…</span>}
          {player.skipCount > 0 && <span style={{ color: '#e74c3c', marginLeft: '2px' }}>⏭</span>}
        </span>

        {/* Score — right aligned, small */}
        <span style={{ fontSize: '0.6rem', color: 'rgba(212,168,67,0.55)', fontFamily: '"Cinzel",serif', flexShrink: 0 }}>
          {player.score}pt
        </span>
      </div>

      {/* Key info row: Phase (PRIMARY) + Hand count (PRIMARY) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {/* Phase badge — DOMINANT */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          background: player.completedPhaseThisRound
            ? 'rgba(39,174,96,0.18)'
            : 'rgba(212,168,67,0.12)',
          border: `1px solid ${player.completedPhaseThisRound ? 'rgba(39,174,96,0.5)' : 'rgba(212,168,67,0.35)'}`,
          borderRadius: '6px',
          padding: '2px 7px',
          flex: '1 1 0',
          minWidth: 0,
        }}>
          <span style={{
            fontSize: '1.05rem', fontWeight: '800',
            fontFamily: '"Cinzel",serif',
            color: player.completedPhaseThisRound ? '#27ae60' : '#d4a843',
            lineHeight: 1.1,
            letterSpacing: '0.02em',
          }}>
            P{player.currentPhase}{player.completedPhaseThisRound ? '✓' : ''}
          </span>
          <span style={{ fontSize: '0.5rem', color: 'rgba(245,234,210,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Phase
          </span>
        </div>

        {/* Hand count badge — DOMINANT */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          background: player.handCount <= 3
            ? 'rgba(231,76,60,0.18)'
            : 'rgba(255,255,255,0.06)',
          border: `1px solid ${player.handCount <= 3 ? 'rgba(231,76,60,0.5)' : 'rgba(255,255,255,0.1)'}`,
          borderRadius: '6px',
          padding: '2px 7px',
          flex: '1 1 0',
          minWidth: 0,
        }}>
          <span style={{
            fontSize: '1.05rem', fontWeight: '800',
            fontFamily: '"Cinzel",serif',
            color: player.handCount <= 3 ? '#e74c3c' : '#f5ead2',
            lineHeight: 1.1,
          }}>
            {player.handCount}
          </span>
          <span style={{ fontSize: '0.5rem', color: 'rgba(245,234,210,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Cards
          </span>
        </div>
      </div>

      {/* Laid down phase - with hit drop zones (compact) */}
      {player.laidDownPhase && (
        <div className="mt-1 space-y-1">
          <div className="flex flex-col gap-2">
            {player.laidDownPhase.map((group, groupIdx) => (
              <HitDropZone
                key={groupIdx}
                playerId={player.id}
                groupIndex={groupIdx}
                group={group}
                phaseInfo={phaseInfo}
                canHitEnabled={canHitEnabled}
                checkCanHit={checkCanHit}
                selectedCard={selectedCard}
                onHit={onHit}
                compact={true}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HitDropZone({
  playerId,
  groupIndex,
  group,
  phaseInfo,
  canHitEnabled,
  checkCanHit,
  selectedCard,
  onHit,
  compact = false
}) {
  // Check if this is a run type
  const groupType = phaseInfo?.requirements?.[groupIndex];
  const isRunType = groupType && (
    groupType.type === 'run' ||
    groupType.type === 'colorRun' ||
    groupType.type === 'oddRun' ||
    groupType.type === 'evenRun' ||
    groupType.type === 'oddColorRun' ||
    groupType.type === 'evenColorRun'
  );

  // Check if selected card or dragged card is a wild
  const selectedIsWild = selectedCard?.type === 'wild';

  // For non-wild cards or non-runs, use simple drop zone
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.CARD,
    canDrop: (item) => {
      if (!canHitEnabled || !phaseInfo) return false;
      // For wilds on runs, we'll handle in the position-specific drop zones
      if (isRunType && item.card.type === 'wild') return false;
      return checkCanHit(item.card, group, groupIndex);
    },
    drop: (item) => {
      onHit(playerId, item.card.id, groupIndex);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  }), [canHitEnabled, phaseInfo, checkCanHit, group, groupIndex, playerId, onHit, isRunType]);

  // Drop zone for start position (wild on runs)
  const [{ isOver: isOverStart, canDrop: canDropStart }, dropStart] = useDrop(() => ({
    accept: ItemTypes.CARD,
    canDrop: (item) => {
      if (!canHitEnabled || !phaseInfo || !isRunType) return false;
      if (item.card.type !== 'wild') return false;
      return checkCanHit(item.card, group, groupIndex);
    },
    drop: (item) => {
      onHit(playerId, item.card.id, groupIndex, 'start');
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  }), [canHitEnabled, phaseInfo, checkCanHit, group, groupIndex, playerId, onHit, isRunType]);

  // Drop zone for end position (wild on runs)
  const [{ isOver: isOverEnd, canDrop: canDropEnd }, dropEnd] = useDrop(() => ({
    accept: ItemTypes.CARD,
    canDrop: (item) => {
      if (!canHitEnabled || !phaseInfo || !isRunType) return false;
      if (item.card.type !== 'wild') return false;
      return checkCanHit(item.card, group, groupIndex);
    },
    drop: (item) => {
      onHit(playerId, item.card.id, groupIndex, 'end');
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  }), [canHitEnabled, phaseInfo, checkCanHit, group, groupIndex, playerId, onHit, isRunType]);

  const canHitWithSelected = selectedCard && checkCanHit(selectedCard, group, groupIndex);
  const showWildPositions = isRunType && (selectedIsWild || canDropStart || canDropEnd);

  // Get group type label for clarity (prefer the official phase definition label)
  const groupLabel = groupType?.label || (
      groupType?.type === 'set' ? 'Set'
    : groupType?.type === 'run' ? 'Run'
    : groupType?.type === 'color' ? 'Color'
    : groupType?.type === 'colorRun' ? 'Color Run'
    : groupType?.type === 'colorSet' ? 'Color Set'
    : groupType?.type?.includes('odd') ? 'Odd Run'
    : groupType?.type?.includes('even') ? 'Even Run'
    : 'Group'
  );

  return (
    <div className="flex flex-col gap-1">
      {/* Group label for clarity between multiple groups */}
      <div className="text-[9px] text-gray-500 text-center">
        {groupLabel} {groupIndex + 1}
      </div>

      <div className="flex items-center gap-2">
        {/* Start position drop zone for wilds on runs - larger hit area */}
        {showWildPositions && (
          <div
            ref={dropStart}
            onClick={() => selectedIsWild && canHitWithSelected && onHit(playerId, selectedCard.id, groupIndex, 'start')}
            className={`
              border-2 border-dashed rounded-lg flex items-center justify-center
              font-bold cursor-pointer transition-all
              ${compact ? 'w-8 h-10 sm:w-10 sm:h-12 text-sm' : 'w-12 h-16 sm:w-16 sm:h-24 text-xl sm:text-2xl'}
              ${isOverStart && canDropStart
                ? 'border-blue-400 text-blue-400 bg-blue-500/40 scale-105 shadow-lg shadow-blue-500/30'
                : canHitWithSelected && selectedIsWild
                  ? 'border-blue-400 text-blue-400 hover:bg-blue-500/30 hover:scale-105'
                  : 'border-gray-500/50 text-gray-500'}
            `}
            title="Place wild at start (lower value)"
          >
            ←
          </div>
        )}

        {/* Main group with cards - larger padding for easier drops */}
        <div
          ref={drop}
          className={`
            flex flex-wrap gap-0 p-[2px] sm:p-1 rounded-lg transition-all min-h-[44px] min-w-[44px]
            ${isOver && canDrop
              ? 'bg-green-500/40 ring-2 ring-green-400 shadow-lg shadow-green-500/30 scale-105'
              : canDrop && !isOver
                ? 'bg-green-500/15 ring-1 ring-green-500/50'
                : 'bg-white/5'}
          `}
        >
          {group.map(card => (
            compact ? (
              <MiniCard key={card.id} card={card} />
            ) : (
              <Card key={card.id} card={card} draggable={false} disabled={true} noShadow={true} />
            )
          ))}
          {/* Hit button for non-wild cards - larger for easier tapping */}
          {(canHitWithSelected && !selectedIsWild) || (isOver && canDrop) ? (
            <button
              onClick={() => selectedCard && !selectedIsWild && onHit(playerId, selectedCard.id, groupIndex)}
              disabled={!canHitWithSelected || selectedIsWild}
              className={`
                border-2 border-dashed rounded-lg flex items-center justify-center
                font-bold transition-all
                ${compact ? 'w-8 h-10 text-sm' : 'w-12 h-16 sm:w-16 sm:h-24 text-xl sm:text-2xl'}
                ${isOver && canDrop
                  ? 'border-green-400 text-green-400 bg-green-500/30 scale-105'
                  : 'border-accent-gold text-accent-gold hover:bg-accent-gold/20 hover:scale-105'}
              `}
              title="Hit here"
            >
              +
            </button>
          ) : null}
        </div>

        {/* End position drop zone for wilds on runs - larger hit area */}
        {showWildPositions && (
          <div
            ref={dropEnd}
            onClick={() => selectedIsWild && canHitWithSelected && onHit(playerId, selectedCard.id, groupIndex, 'end')}
            className={`
              border-2 border-dashed rounded-lg flex items-center justify-center
              font-bold cursor-pointer transition-all
              ${compact ? 'w-8 h-10 sm:w-10 sm:h-12 text-sm' : 'w-12 h-16 sm:w-16 sm:h-24 text-xl sm:text-2xl'}
              ${isOverEnd && canDropEnd
                ? 'border-purple-400 text-purple-400 bg-purple-500/40 scale-105 shadow-lg shadow-purple-500/30'
                : canHitWithSelected && selectedIsWild
                  ? 'border-purple-400 text-purple-400 hover:bg-purple-500/30 hover:scale-105'
                  : 'border-gray-500/50 text-gray-500'}
            `}
            title="Place wild at end (higher value)"
          >
            →
          </div>
        )}
      </div>
    </div>
  );
}

// Drop zone wrapper for the player's hand - accepts cards dragged from phase builder
function HandDropZone({ children, onReturnCard, hasBuilder }) {
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.CARD,
    canDrop: (item) => item.fromBuilder === true,
    drop: (item) => {
      if (item.fromBuilder && onReturnCard) {
        onReturnCard(item.card, item.builderGroupIndex);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  }), [onReturnCard]);

  return (
    <div
      ref={drop}
      className={`
        border-t border-white/10 p-2 sm:p-4 bg-white/5 transition-all
        ${isOver && canDrop ? 'bg-blue-500/20 ring-2 ring-blue-400' : ''}
        ${canDrop && !isOver ? 'ring-1 ring-dashed ring-blue-400/50' : ''}
      `}
    >
      {children}
      {/* Hint when dragging from builder */}
      {canDrop && (
        <div className="text-center text-blue-400 text-xs mt-2 animate-pulse">
          {isOver ? 'Drop to return to hand' : 'Drag here to return card to hand'}
        </div>
      )}
    </div>
  );
}

export default GameBoard;
